/**
 * Helper utility for parsing dimension tags, person mentions, file mentions, and highlight tags
 *
 * Supports multiple formats:
 * - Single tag: [DIMENSION:50:Stress sensitivity]
 * - Single tag without label: [DIMENSION:50]
 * - Multiple tags: [DIMENSION:50, DIMENSION:67]
 * - Mixed tags: [DIMENSION:50, PERSON:1294]
 * - Tags with labels: [DIMENSION:50, FILE:234:Cv of person]
 */

/**
 * Tag type enum for type safety
 */
export enum TagType {
  DIMENSION = "DIMENSION",
  DIMENSION_HIGHLIGHT = "DIMENSION_HIGHLIGHT",
  PERSON = "PERSON",
  FILE = "FILE",
  ASSESSMENT_REPORT = "ASSESSMENT_REPORT",
  ASSESSMENT_PSYCH = "ASSESSMENT_PSYCH",
  PROJECT_DESCRIPTION = "PROJECT_DESCRIPTION",
}

/**
 * Base interface for all parsed tags
 */
export interface ParsedTag {
  type: TagType;
}

export interface ParsedDimensionTag extends ParsedTag {
  type: TagType.DIMENSION;
  highlight?: boolean;
  dim_id: number;
  text: string;
}

export interface ParsedPersonMention extends ParsedTag {
  type: TagType.PERSON;
  person_id: number;
  person_name: string;
}

export interface ParsedFileTag extends ParsedTag {
  type: TagType.FILE;
  file_id: number;
  file_name: string;
}

export interface ParsedReferenceTag extends ParsedTag {
  type:
    | TagType.ASSESSMENT_REPORT
    | TagType.ASSESSMENT_PSYCH
    | TagType.PROJECT_DESCRIPTION;
}

/**
 * Union type for all possible parsed tags
 */
export type AnyParsedTag =
  | ParsedDimensionTag
  | ParsedPersonMention
  | ParsedFileTag
  | ParsedReferenceTag;

/**
 * Represents a tag group - either a single tag or multiple tags within brackets
 */
export interface TagGroup {
  originalText: string;
  tags: AnyParsedTag[];
}

/**
 * Reference tag types that don't require an ID
 */
const REFERENCE_TAG_TYPES = [
  TagType.ASSESSMENT_REPORT,
  TagType.ASSESSMENT_PSYCH,
  TagType.PROJECT_DESCRIPTION,
] as const;

/**
 * Parses a single tag string into its components
 * @param tagString - The tag string (e.g., "DIMENSION:15:embracing change" or "DIMENSION:15")
 * @returns Parsed tag object or null if parsing fails
 */
export const parseSingleTag = (tagString: string): AnyParsedTag | null => {
  // Remove any leading/trailing whitespace
  const trimmed = tagString.trim();

  // First, try to match reference tags (just TYPE, no colon or content)
  if (REFERENCE_TAG_TYPES.includes(trimmed as any)) {
    return {
      type: trimmed as ParsedReferenceTag["type"],
    };
  }

  // Match pattern: TYPE:ID or TYPE:ID:LABEL
  // Pattern explanation: ([A-Z_]+):(\d+)(?::([^,\]]+))?
  // - ([A-Z_]+) - captures tag type (DIMENSION, PERSON, FILE, etc.)
  // - : - literal colon separator
  // - (\d+) - captures numeric ID
  // - (?::([^,\]]+))? - optionally captures label after another colon (non-greedy, stops at comma or bracket)
  const match = trimmed.match(/^([A-Z_]+):(\d+)(?::(.+))?$/);

  if (!match) {
    return null;
  }

  const [, tagTypeStr, idStr, label] = match;
  const id = parseInt(idStr, 10);

  // Parse based on tag type
  switch (tagTypeStr) {
    case TagType.DIMENSION:
      return {
        type: TagType.DIMENSION,
        dim_id: id,
        text: label?.trim() || "",
      };

    case TagType.DIMENSION_HIGHLIGHT:
      return {
        type: TagType.DIMENSION,
        highlight: true,
        dim_id: id,
        text: label?.trim() || "",
      };

    case TagType.PERSON:
      return {
        type: TagType.PERSON,
        person_id: id,
        person_name: label?.trim() || "",
      };

    case TagType.FILE:
      return {
        type: TagType.FILE,
        file_id: id,
        file_name: label?.trim() || "",
      };

    default:
      // Unknown tag type
      return null;
  }
};

/**
 * Parses the content within brackets, handling both single and multiple tags
 * @param bracketContent - The content inside brackets (e.g., "DIMENSION:15" or "DIMENSION:15, PERSON:123")
 * @returns Array of parsed tags
 */
export const parseTagsInBracket = (bracketContent: string): AnyParsedTag[] => {
  // Split by comma to handle multiple tags
  const tagStrings = bracketContent.split(",");

  const parsedTags: AnyParsedTag[] = [];

  for (const tagString of tagStrings) {
    const parsed = parseSingleTag(tagString);
    if (parsed) {
      parsedTags.push(parsed);
    }
  }

  return parsedTags;
};

/**
 * Parses a complete tag group (content within brackets)
 * @param tagGroupString - The complete tag string including brackets (e.g., "[DIMENSION:15:text]")
 * @returns TagGroup object or null if parsing fails
 */
export const parseTagGroup = (tagGroupString: string): TagGroup | null => {
  // Extract content within brackets
  const match = tagGroupString.match(/^\[(.+)\]$/);

  if (!match) {
    return null;
  }

  const bracketContent = match[1];
  const tags = parseTagsInBracket(bracketContent);

  if (tags.length === 0) {
    return null;
  }

  return {
    originalText: tagGroupString,
    tags,
  };
};

/**
 * Legacy function - kept for backward compatibility
 * Parses a dimension tag string into its components
 * @deprecated Use parseSingleTag instead
 */
export const parseDimensionTag = (tag: string): ParsedDimensionTag | null => {
  const match = tag.match(/\[DIMENSION:(\d+)(?::([^\]]+))?\]/);

  if (match) {
    const [, dimId, text] = match;

    return {
      type: TagType.DIMENSION,
      dim_id: parseInt(dimId),
      text: text || "",
    };
  }

  return null;
};

/**
 * Legacy function - kept for backward compatibility
 * @deprecated Use parseSingleTag instead
 */
export const parseDimensionHighlightTag = (
  tag: string
): ParsedDimensionTag | null => {
  const match = tag.match(/\[DIMENSION_HIGHLIGHT:(\d+)(?::([^\]]+))?\]/);

  if (match) {
    const [, dimId, text] = match;

    return {
      type: TagType.DIMENSION,
      highlight: true,
      dim_id: parseInt(dimId),
      text: text || "",
    };
  }

  return null;
};

/**
 * Legacy function - kept for backward compatibility
 * @deprecated Use parseSingleTag instead
 */
export const parsePersonMention = (tag: string): ParsedPersonMention | null => {
  const match = tag.match(/\[PERSON:(\d+)(?::([^\]]+))?\]/);

  if (match) {
    const [, personId, personName] = match;

    return {
      type: TagType.PERSON,
      person_id: parseInt(personId),
      person_name: personName || "",
    };
  }

  return null;
};

/**
 * Legacy function - kept for backward compatibility
 * @deprecated Use parseSingleTag instead
 */
export const parseFileTag = (tag: string): ParsedFileTag | null => {
  const match = tag.match(/\[FILE:(\d+)(?::([^\]]+))?\]/);
  if (match) {
    const [, fileId, fileName] = match;
    return {
      type: TagType.FILE,
      file_id: parseInt(fileId),
      file_name: fileName || "",
    };
  }
  return null;
};

/**
 * Checks if a string contains dimension tags
 */
export const hasDimensionTags = (text: string): boolean =>
  text.includes("[DIMENSION:") || text.includes("[DIMENSION_HIGHLIGHT:");

/**
 * Checks if a string contains person mention tags
 */
export const hasPersonMentions = (text: string): boolean =>
  text.includes("[PERSON:");

/**
 * Checks if a string contains file tags
 */
export const hasFileTags = (text: string): boolean => text.includes("[FILE:");

/**
 * Checks if a string contains reference tags (ASSESSMENT_REPORT, ASSESSMENT_PSYCH, PROJECT_DESCRIPTION)
 */
export const hasReferenceTags = (text: string): boolean =>
  text.includes("[ASSESSMENT_REPORT]") ||
  text.includes("[ASSESSMENT_PSYCH]") ||
  text.includes("[PROJECT_DESCRIPTION]");

/**
 * Checks if a string contains any custom tags
 */
export const hasCustomTags = (text: string): boolean => {
  return (
    hasDimensionTags(text) ||
    hasPersonMentions(text) ||
    hasFileTags(text) ||
    hasReferenceTags(text)
  );
};

/**
 * Splits text into parts, separating tag groups from regular text
 * This is the new improved version that handles multiple tags within brackets
 * @param text - The text to split
 * @returns Array of strings and TagGroup objects
 */
export const splitTextByTagGroups = (
  text: string
): Array<string | TagGroup> => {
  // Updated regex to match brackets containing any combination of tags
  // Pattern matches:
  // - Tags with content: [DIMENSION:50], [PERSON:123:Name], etc.
  // - Reference tags without content: [ASSESSMENT_REPORT], [ASSESSMENT_PSYCH], [PROJECT_DESCRIPTION]
  const regex =
    /\[(?:(?:DIMENSION_HIGHLIGHT|DIMENSION|PERSON|FILE):[^\]]+|ASSESSMENT_REPORT|ASSESSMENT_PSYCH|PROJECT_DESCRIPTION)\]/g;

  const parts: Array<string | TagGroup> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Parse the tag group
    const tagGroup = parseTagGroup(match[0]);
    if (tagGroup) {
      parts.push(tagGroup);
    } else {
      // If parsing fails, include as regular text
      parts.push(match[0]);
    }

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
};

/**
 * Legacy function - kept for backward compatibility
 * Splits text into parts, separating individual tags from regular text
 * @deprecated Use splitTextByTagGroups for better support of multiple tags
 */
export const splitTextByTags = (
  text: string
): Array<string | ParsedDimensionTag | ParsedPersonMention | ParsedFileTag> => {
  const parts = text.split(
    /(\[DIMENSION:\d+(?::[^\]]+)?\]|\[DIMENSION_HIGHLIGHT:\d+(?::[^\]]+)?\]|\[PERSON:\d+(?::[^\]]+)?\]|\[FILE:\d+(?::[^\]]+)?\])/
  );

  return parts.map((part) => {
    if (part.startsWith("[DIMENSION_HIGHLIGHT:")) {
      const parsed = parseDimensionHighlightTag(part);
      return parsed || part;
    }

    if (part.startsWith("[DIMENSION:")) {
      const parsed = parseDimensionTag(part);
      return parsed || part;
    }

    if (part.startsWith("[PERSON:")) {
      const parsed = parsePersonMention(part);
      return parsed || part;
    }

    if (part.startsWith("[FILE:")) {
      const parsed = parseFileTag(part);
      return parsed || part;
    }

    return part;
  });
};
